/**
 * KonvaMarkersLayerContainer - Container Component
 *
 * Container component responsible for:
 * - Zustand store connection (useStore, useStorePrimitive, useActions, useImageLibraryStore)
 * - Computing marker items from party roster, NPCs, and POIs
 * - Building the image URL map from the image library
 * - Delegating presentation to the pure KonvaMarkersLayer component
 *
 * This follows the Container/Presenter pattern as defined in ADR-034.
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React, { useMemo } from 'react';
import { useStore, useStorePrimitive, useActions } from '../../hooks/useStore';
import { useImageLibraryStore } from '../../state/createImageLibraryStore';
import { useStableCallback } from '../../hooks/hookUtils';
import type { PartyMember, NPC, POI, Image, RenderedImageRect } from '../../types/types';
import KonvaMarkersLayer from './KonvaMarkersLayer';
import type { MarkerItem } from './KonvaMarkersLayer';

interface KonvaMarkersLayerContainerProps {
  imageRect: RenderedImageRect;
}

const emptyParty: PartyMember[] = [];
const emptyNpcs: NPC[] = [];
const emptyPois: POI[] = [];
const emptyImages: Image[] = [];

const KonvaMarkersLayerContainer: React.FC<KonvaMarkersLayerContainerProps> = ({ imageRect }) => {
  const selectedMarkerId = useStorePrimitive(state => state.session?.map?.selectedMarkerId);
  const partyRoster = useStore(state => state.session?.roster?.partyRoster) || emptyParty;
  const npcs = useStore(state => state.session?.roster?.npcs) || emptyNpcs;
  const pois = useStore(state => state.session?.roster?.pois) || emptyPois;
  const activeMapId = useStorePrimitive(state => state.session?.map?.activeMapId);
  const imageLibrary = useImageLibraryStore(state => state.images) || emptyImages;
  const highlightedMarkerId = useStorePrimitive(state => state.session?.map?.highlightedMarkerId);
  const showMapMarkers = useStorePrimitive(state => state.settings?.showMapMarkers);
  const { updateMarkerPosition, setSelectedMarker } = useActions();

  const imageMap = useMemo(
    () => new Map((imageLibrary ?? []).map((img: Image) => [img.id, img.imageUrl])),
    [imageLibrary]
  );

  const markers = useMemo((): MarkerItem[] => {
    const playerMarkers: MarkerItem[] = (partyRoster ?? [])
      .filter((p: PartyMember) => p.mapId === activeMapId)
      .map((p: PartyMember) => ({ ...p, itemType: 'player' as const }));
    const npcMarkers: MarkerItem[] = (npcs ?? [])
      .filter((n: NPC) => n.showOnMap && n.mapId === activeMapId)
      .map((n: NPC) => ({ ...n, itemType: 'npc' as const }));
    const poiMarkers: MarkerItem[] = (pois ?? [])
      .filter((p: POI) => p.showOnMap && p.mapId === activeMapId)
      .map((p: POI) => ({ ...p, itemType: 'poi' as const }));
    return [...playerMarkers, ...npcMarkers, ...poiMarkers];
  }, [partyRoster, npcs, pois, activeMapId]);

  // Stable callback to avoid re-creating on every render.
  // Wraps the store action for marker position updates after drag.
  const handleUpdateMarkerPosition = useStableCallback(
    (type: 'player' | 'npc' | 'poi', id: number, x: number, y: number) => {
      if (updateMarkerPosition) updateMarkerPosition(type, id, x, y);
    }
  );

  // Stable callback for marker selection toggling.
  const handleSetSelectedMarker = useStableCallback((markerId: number | null) => {
    if (setSelectedMarker) setSelectedMarker(markerId);
  });

  if (!showMapMarkers) {
    return null;
  }

  return (
    <KonvaMarkersLayer
      imageRect={imageRect}
      markers={markers}
      imageMap={imageMap}
      selectedMarkerId={selectedMarkerId}
      highlightedMarkerId={highlightedMarkerId}
      onUpdateMarkerPosition={handleUpdateMarkerPosition}
      onSetSelectedMarker={handleSetSelectedMarker}
    />
  );
};

export default KonvaMarkersLayerContainer;
